import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import { differenceInYears } from 'date-fns';
import { supabase } from '../../api';

interface ProfileDetails {
  id?: string;
  avatar?: string;
  bg_cover?: string;
  birthday?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  full_bio?: string;
  country?: string;
  state?: string;
  zip_code?: number | null;
  expert_id?: number;
  is_first_investing?: boolean;
  new_email?: string;
  phone?: string;
  is_hidden?: boolean;
}

interface Profile extends ProfileDetails {
  id: string;
}

export const updateUser = async (profile: Profile) => {
  console.log(profile);
  if (profile.username) {
    const { error } = await supabase
      .from('user_profiles')
      .update({ username: profile.username })
      .eq('id', profile.id);

    if (error) {
      console.log(error.message);
    }
  }

  if (profile?.birthday) {
    const { error: updateBirthdayError } = await supabase
      .from('user_profiles')
      .update({ birthday: profile?.birthday })
      .eq('id', profile.id);

    if (updateBirthdayError) {
      console.log(updateBirthdayError.message);
    }
  }

  if (profile?.phone) {
    const { error: updatePhoneError } = await supabase
      .from('user_profiles')
      .update({ phone: profile?.phone })
      .eq('id', profile.id);

    if (updatePhoneError) {
      console.log(updatePhoneError.message);
    }
  }

  if (profile?.new_email) {
    const { error } = await supabase.auth.updateUser({ email: profile?.new_email });

    if (error) {
      console.log(error.message);
    }

    const { error: updateEmailError } = await supabase
      .from('user_profiles')
      .update({ email: profile?.new_email })
      .eq('id', profile.id);

    if (updateEmailError) {
      console.log(updateEmailError.message);
    }
  }

  if (profile?.country) {
    const { error: updateCountryError } = await supabase
      .from('user_profiles')
      .update({ country: profile?.country })
      .eq('id', profile.id);

    if (updateCountryError) {
      console.log(updateCountryError.message);
    }
  }

  // update bd
  const requests: any[] = [];
  if (Object.keys(profile).some(k => k !== 'id')) {
    requests.push(supabase.from('user_profiles').upsert(profile).eq('id', profile.id));
  }

  if (requests.length) {
    const responses = await Promise.all(requests);
    const responsesError = responses.find(v => v.error);

    if (responsesError) {
      throw new Error(responsesError.error?.message);
    }
  }

  const { data: newData, error: newError } = await supabase
    .from('user_profiles')
    .select(
      `
      *,
      expertProfile:salons_profile (*)
    `,
    )
    .eq('id', profile.id)
    .single();

  if (newError) {
    throw new Error(newError.message);
  }

  const { data: session } = await supabase.auth.getSession();
  newData.isOwnProfile = session?.session?.user.id === profile.id;

  return newData;
};

export default function useUpdateUser(profile?: Profile) {
  const queryClient = useQueryClient();

  return useMutation(
    (profileDetails: ProfileDetails | void) =>
      updateUser(
        profileDetails ? { id: profile?.id || '', ...profileDetails } : profile || { id: '' },
      ),
    {
      onSuccess: d => {
        const { isOwnProfile, ...data } = d;
        queryClient.setQueryData(['profile', profile?.id], data);
        if (isOwnProfile) {
          queryClient.setQueryData(['profile'], data);
        }
      },
    },
  );
}
